import { supabase } from '../lib/supabase'
import {db} from '../db';
import {syncvalue,timeFromStorage, timeToStorage} from "../utils/datetime";

const uploadFacilityLogo = async (file, facilityId) => {
    const ext = file.name.split('.').pop();
    const path = `${facilityId}/logo.${ext}`;
    const { error } = await supabase.storage
        .from('facility-logos')
        .upload(path, file, { upsert: true });
    if (error) throw new Error(`Logo upload failed: ${error.message}`);
    const { data } = supabase.storage.from('facility-logos').getPublicUrl(path);
    return data.publicUrl;
};

const normalizeWorkdayTimeframes = (timeframes, fallbackStart, fallbackEnd) => {
    const normalizeTimeframe = (cur) => {
        const start = timeFromStorage(cur.start_of_day);
        let end = timeFromStorage(cur.end_of_day);
        if (start && end && end.isBefore(start)) {
            end = end.add(1, 'day');
        }
        return {
            ...cur,
            start_of_day: start,
            end_of_day: end,
        };
    };

    if (Array.isArray(timeframes) && timeframes.length > 0) {
        return timeframes.map((cur)=>normalizeTimeframe(cur));
    }
    if (fallbackStart || fallbackEnd) {
        const fallback = normalizeTimeframe({start_of_day: fallbackStart || null, end_of_day: fallbackEnd || null});
        return [fallback];
    }
    return [];
};

const timeframesToStorage = (timeframes) => {
    if (!Array.isArray(timeframes)) return timeframes;
    return timeframes.map((cur)=>({
        ...cur,
        start_of_day: timeToStorage(cur.start_of_day),
        end_of_day: timeToStorage(cur.end_of_day),
    }));
};

const applyPrimaryTimeframe = (record) => {
    const primary = record?.workday_timeframes?.[0];
    if (primary?.start_of_day) record.start_of_day = primary.start_of_day;
    if (primary?.end_of_day) record.end_of_day = primary.end_of_day;
};

const transformFromDb = (inputRecord) => {
    const temp = {...inputRecord};
    temp['start_of_day'] = timeFromStorage(temp['start_of_day']);
    temp['end_of_day'] = timeFromStorage(temp['end_of_day']);
    temp['workday_timeframes'] = normalizeWorkdayTimeframes(temp['workday_timeframes'], temp['start_of_day'], temp['end_of_day']);
    temp['review_period'] = (temp['review_period'] === null) ? 1 : temp['review_period'];
    return temp;
}

const transformToDb = (inputRecord) => {
    const temp = {...inputRecord};
    applyPrimaryTimeframe(temp);
    temp['start_of_day'] = timeToStorage(temp['start_of_day']);
    temp['end_of_day'] = timeToStorage(temp['end_of_day']);
    temp['workday_timeframes'] = timeframesToStorage(temp['workday_timeframes']);
    return {facility: temp};
}

const toDexie = (inputRecord) => {
    const temp = {...inputRecord};
    applyPrimaryTimeframe(temp);
    temp['start_of_day'] = timeToStorage(temp['start_of_day']);
    temp['end_of_day'] = timeToStorage(temp['end_of_day']);
    temp['workday_timeframes'] = timeframesToStorage(temp['workday_timeframes']);
    return temp;
}

const fromDexie = (inputRecord) => {
    const temp = {...inputRecord};
    temp['start_of_day'] = timeFromStorage(temp['start_of_day']);
    temp['end_of_day'] = timeFromStorage(temp['end_of_day']);
    temp['workday_timeframes'] = normalizeWorkdayTimeframes(temp['workday_timeframes'], temp['start_of_day'], temp['end_of_day']);
    temp['review_period'] = (temp['review_period'] === null) ? 1 : temp['review_period'];
    return temp;
}




const fetchFacility = async() => {
    try {
        const { data, error } = await supabase
            .from('facility')
            .select('*')
        if (error) throw new Error(`Error ${error.code}: ${error.message}`);
        if (data !== null) {
            return transformFromDb(data[0]);
        }
    } catch (error) {
        console.error(error);
        throw error
    }
    throw new Error("Facility for logged in user not found");
};

const getFacilityLiveQuery = () => {
    return db().facility.toCollection().first();
};

const getFacility = async() => {
    try {

        const temp = await db().facility.toCollection().first();
        return (temp) ? fromDexie(temp) : undefined;

    } catch (error) {
        console.error(error);
        throw error;
    }
};

const writeFacilityToSupabase = async(inputRecord) => {
    try {
        const {facility} = transformToDb(inputRecord);
        const updateResult = await supabase
            .from('facility')
            .update(facility)
            .eq('id',facility.id)
        if (updateResult.error) throw new Error(`Error ${updateResult.error.code}: ${updateResult.error.message}`);
        return updateResult;
    } catch (error) {
        console.error(error);
        throw error
    }

}

const writeFacilityToDexie = async(inputRecord) => {
    try {
        const cleanRecord = toDexie(inputRecord);
        const id = await db().facility.put(cleanRecord);
        return id;
    } catch (error) {
        console.error(error);
        throw error;
    }

}

const saveFacility = async(inputRecord,isOnline) => {
    try {
        const syncValue = syncvalue();
        // we should always have an id, so not setting it here.
        inputRecord.updated_at = new Date();
        inputRecord.sync = syncValue;

        const dexieResults = await writeFacilityToDexie(inputRecord);
        const supabaseResults = (isOnline) ? await writeFacilityToSupabase(inputRecord) : {status: "offline"};

        return {dexieResults,supabaseResults};

    } catch (error) {
        console.error(error);
        throw error;
    }
};

const sync = async() => {
    const results = {
        localWrites: [],
        remoteWrites: [],
        syncsEqual: [],
    };

    const remoteData = await fetchFacility();


    const localData = await getFacility();


    if (localData === undefined) {
        // if local data is missing, write remote data to local
        const id = await writeFacilityToDexie(remoteData);
        results.localWrites.push(id);
    } else {
        // if both present, compare sync values and write whichever is later to the other

        if (localData.sync > remoteData.sync) {
            results.remoteWrites.push(await writeFacilityToSupabase(localData));
        } else if (remoteData.sync > localData.sync) {
            results.localWrites.push(await writeFacilityToDexie(remoteData));
        } else {
            results.syncsEqual.push(remoteData.id);
        }
    }

    return results;
};

const truncateDexie = async() => {
    return await db().facility.clear();
}

export {fetchFacility, getFacility, getFacilityLiveQuery, saveFacility, uploadFacilityLogo, sync, truncateDexie};
